import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChevronRight, Loader2, SearchX } from "lucide-react";
import { useListControls, ListToolbar, ListPagination } from "@/components/ListControls";

const scoreColor = (s) => (s >= 7 ? "text-[#00A859]" : s >= 4.5 ? "text-[#F59E0B]" : "text-destructive");

const empty = { name: "", vendor: "", category: "", description: "", cost_monthly: 0, url: "", scores: {}, notes: "" };

export default function ToolsPage() {
  const [tools, setTools] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const navigate = useNavigate();
  const { canEdit } = useAuth();
  const { activeClientId, activeClient } = useClient();

  const controls = useListControls(tools, {
    searchFields: ["name", "vendor", "category"],
    filters: { category: { label: "Categoria", get: (t) => t.category } },
    sortOptions: [
      { value: "score_desc", label: "Punteggio ↓", cmp: (a, b) => b.overall_score - a.overall_score },
      { value: "score_asc", label: "Punteggio ↑", cmp: (a, b) => a.overall_score - b.overall_score },
      { value: "name", label: "Nome A–Z", cmp: (a, b) => (a.name || "").localeCompare(b.name || "") },
      { value: "cost_desc", label: "Costo ↓", cmp: (a, b) => (b.cost_monthly || 0) - (a.cost_monthly || 0) },
    ],
    pageSize: 12,
  });

  const load = async () => {
    const params = activeClientId ? { client_id: activeClientId } : {};
    const [t, c] = await Promise.all([api.get("/tools", { params }), api.get("/criteria")]);
    setTools(t.data);
    setCriteria(c.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [activeClientId]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (t) => {
    setForm({ ...empty, ...t, scores: t.scores || {} });
    setEditId(t.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Il nome è obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = { ...form, cost_monthly: parseFloat(form.cost_monthly) || 0, client_id: editId ? form.client_id ?? activeClientId : activeClientId };
      if (editId) await api.put(`/tools/${editId}`, payload);
      else await api.post("/tools", payload);
      toast.success(editId ? "Tool aggiornato" : "Tool creato");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Eliminare questo tool?")) return;
    await api.delete(`/tools/${id}`);
    toast.success("Tool eliminato");
    load();
  };

  return (
    <Layout>
      <PageHeader title="Tool IT" subtitle={activeClient ? `${activeClient.name} · ${tools.length} tool` : `Portfolio · ${tools.length} tool valutati`}>
        {canEdit && (
          <Button data-testid="add-tool-btn" onClick={openNew} className="rounded-none">
            <Plus className="w-4 h-4 mr-2" /> Nuovo tool
          </Button>
        )}
      </PageHeader>

      <div className="p-8">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : tools.length === 0 ? (
          <div className="border border-dashed border-border bg-white p-16 text-center">
            <p className="text-muted-foreground">Nessun tool ancora valutato.</p>
            {canEdit && <Button onClick={openNew} className="mt-4 rounded-none"><Plus className="w-4 h-4 mr-2" /> Aggiungi il primo tool</Button>}
          </div>
        ) : (
          <>
            <ListToolbar controls={controls} searchPlaceholder="Cerca tool, vendor, categoria…" />
            {controls.filtered.length === 0 ? (
              <div className="border border-dashed border-border bg-white p-16 text-center" data-testid="tools-no-results">
                <SearchX className="w-10 h-10 text-muted-foreground mx-auto" />
                <p className="text-muted-foreground mt-4">Nessun tool corrisponde ai filtri.</p>
              </div>
            ) : (
            <div className="border border-border bg-white" data-testid="tools-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="data-label px-4 py-3">#</th>
                  <th className="data-label px-4 py-3">Tool</th>
                  <th className="data-label px-4 py-3">Vendor</th>
                  <th className="data-label px-4 py-3">Categoria</th>
                  <th className="data-label px-4 py-3 text-right">Costo/mese</th>
                  <th className="data-label px-4 py-3 text-right">Punteggio</th>
                  <th className="data-label px-4 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {controls.visible.map((t, i) => (
                  <tr
                    key={t.id}
                    data-testid={`tool-row-${i}`}
                    onClick={() => navigate(`/tools/${t.id}`)}
                    className={`border-b border-border cursor-pointer hover:bg-secondary transition-colors duration-150 ${i % 2 ? "bg-[#F9FAFB]" : "bg-white"}`}
                  >
                    <td className="px-4 py-3 font-mono text-muted-foreground">{(controls.page - 1) * controls.pageSize + i + 1}</td>
                    <td className="px-4 py-3 font-medium">{t.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{t.vendor || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{t.category || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">€ {t.cost_monthly || 0}</td>
                    <td className={`px-4 py-3 text-right font-mono font-semibold ${scoreColor(t.overall_score)}`}>{t.overall_score}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {canEdit && <button data-testid={`edit-tool-${i}`} onClick={(e) => { e.stopPropagation(); openEdit(t); }} className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>}
                        {canEdit && <button data-testid={`delete-tool-${i}`} onClick={(e) => remove(t.id, e)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            )}
            <ListPagination controls={controls} />
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">{editId ? "Modifica tool" : "Nuovo tool"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="data-label">Nome *</Label><Input data-testid="tool-name-input" className="mt-1 rounded-none" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label className="data-label">Vendor</Label><Input className="mt-1 rounded-none" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></div>
              <div><Label className="data-label">Categoria</Label><Input className="mt-1 rounded-none" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="es. CRM, ERP, Ticketing" /></div>
              <div><Label className="data-label">Costo mensile (€)</Label><Input type="number" className="mt-1 rounded-none font-mono" value={form.cost_monthly} onChange={(e) => setForm({ ...form, cost_monthly: e.target.value })} /></div>
            </div>
            <div><Label className="data-label">Descrizione</Label><Textarea className="mt-1 rounded-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

            <div className="border-t border-border pt-4">
              <div className="data-label mb-3">Punteggi per criterio (0–10)</div>
              <div className="space-y-4">
                {criteria.map((c) => (
                  <div key={c.id}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{c.name} <span className="text-muted-foreground font-mono text-xs">·peso {c.weight}</span></span>
                      <span className="font-mono font-semibold" data-testid={`score-value-${c.name}`}>{form.scores[c.id] ?? 0}</span>
                    </div>
                    <Slider
                      className="mt-2"
                      value={[form.scores[c.id] ?? 0]}
                      min={0} max={10} step={1}
                      onValueChange={([v]) => setForm({ ...form, scores: { ...form.scores, [c.id]: v } })}
                    />
                  </div>
                ))}
                {criteria.length === 0 && <p className="text-sm text-muted-foreground">Nessun criterio definito.</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-tool-btn" className="rounded-none" onClick={save} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
